/**
 * Read-only campaign brief. Shared by the draft wizard preview and the
 * post-submit editor so a brand sees the same fields after they send a
 * version for review as they did on the last wizard step.
 */
import type { ReactNode } from "react";
import { Money } from "@ui/primitives";
import { fromMinorString } from "@contracts/money";
import {
  BRIEF_FIELDS, DRAFT_PLATFORMS, TYPE_LABEL, liveCampaignTotalMinor, moneyToMinor,
  selectedPlatforms, type DraftFormState,
} from "./draftForm";

const minor = (v: string, ccy: string) => fromMinorString(v, ccy as "GBP" | "USD" | "EUR");
const asDate = (iso: string) =>
  iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" }) : "-";

function moneyFromMajor(raw: string, currency: string) {
  const n = moneyToMinor(raw);
  return n == null ? null : minor(String(n), currency);
}

export function VersionPreview({ form, currency, coverUrl }: {
  form: DraftFormState; currency: string; coverUrl?: string | null;
}) {
  const cap = moneyFromMajor(form.cap, currency);
  const postCap = form.post_cap.trim() ? moneyFromMajor(form.post_cap, currency) : null;

  return (
    <dl className="cm-wizard-preview">
      {coverUrl ? (
        <PreviewRow label="Cover">
          <img className="cm-cover-preview-img" src={coverUrl} alt="" />
        </PreviewRow>
      ) : null}
      <PreviewRow label="Name">{form.display_name || "-"}</PreviewRow>
      <PreviewRow label="Summary">{form.summary || "-"}</PreviewRow>
      {form.objective.trim() ? <PreviewRow label="Objective">{form.objective}</PreviewRow> : null}
      <PreviewRow label="Type">{TYPE_LABEL["AUTHORIZED_CLIPPING"]}</PreviewRow>
      <PreviewRow label="Submissions per creator">{form.submissions_per_creator || "-"}</PreviewRow>
      {selectedPlatforms(form).map((id) => {
        const label = DRAFT_PLATFORMS.find((p) => p.id === id)?.label ?? id;
        const budget = moneyFromMajor(form.platform_budgets[id] ?? "", currency);
        const rate = moneyFromMajor(form.platform_cpms[id] ?? "", currency);
        return (
          <PreviewRow key={id} label={label}>
            {budget ? <Money value={budget} /> : "-"} budget,{" "}
            {rate ? <Money value={rate} /> : "-"} per 1,000 views
          </PreviewRow>
        );
      })}
      <PreviewRow label="Cap per creator">{cap ? <Money value={cap} /> : "-"}</PreviewRow>
      {liveCampaignTotalMinor(form) != null ? (
        <PreviewRow label="Campaign total cap">
          <Money value={minor(String(liveCampaignTotalMinor(form)), currency)} />
        </PreviewRow>
      ) : null}
      {postCap ? <PreviewRow label="Cap per post"><Money value={postCap} /></PreviewRow> : null}
      <PreviewRow label="Opening date">{asDate(form.join_opens_at)}</PreviewRow>
      <PreviewRow label="Content submission">{asDate(form.submission_deadline_at)}</PreviewRow>
      <PreviewRow label="Campaign end">{asDate(form.campaign_ends_at)}</PreviewRow>
      <PreviewRow label="Tracking days">{form.tracking_days}</PreviewRow>
      <PreviewRow label="Minimum live days">{form.min_live_days}</PreviewRow>
      <PreviewRow label="Earning hold days">{form.earning_hold_days}</PreviewRow>
      {BRIEF_FIELDS.map((f) => (
        <PreviewRow key={f.key} label={f.label}>{form.brief[f.key] || "-"}</PreviewRow>
      ))}
      {form.source_assets.trim() ? (
        <PreviewRow label="Source files">{form.source_assets}</PreviewRow>
      ) : null}
    </dl>
  );
}

export function PreviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="cm-wizard-preview-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
